import Backbone from 'backbone';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileState, {FileStateAttributes} from 'filestate/FileState';
import * as navigation from 'Navigation';
import StorableResultsTabState from 'results/linked-results/StorableResultsTabState';
import {shouldDisablePrimaryLinkColumn} from 'runs/shouldDisablePrimaryLinkColumn';
import ContainerTestOrdersTabState from 'sample-tests/ui/test-orders/ContainerTestOrdersTabState';
import EntityStore from 'stores/EntityStore';
import {asJQueryPromise} from 'util/Promises';

import * as ContainerDAO from '../ContainerDAO';
import StorableStore from '../StorableStore';
import ContainerRelevantItemsTabState from './ContainerRelevantItemsTabState';
import ContainerTabState from './ContainerTabState';

class ContainerToolbarState extends Backbone.Model {}

export default class ContainerFileState extends FileState {
  static fileType: string;
  static openFile: (fileId: string) => JQueryPromise<ContainerFileState>;

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new ContainerToolbarState(),
      statusbar: this.hasResults()
        ? new statusbar.ContainerStatusBarState()
        : new statusbar.SingleBufferStatusBarState(),
    };
  }

  override editURL(): string {
    return ContainerDAO.containerEditorURL(this.id);
  }

  override name(): string {
    return this.get('name') || 'Loading...';
  }

  override archiveRecord(): ArchiveRecord {
    return this.get('archiveRecord');
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord): void {
    this.set({archiveRecord});
  }

  override permissions(): Permissions {
    return this.get('permissions');
  }

  override setPermissions(permissions: Permissions): void {
    this.set({permissions});
  }

  hasResults(): boolean {
    return (
      isFeatureEnabled({feature: ResultsFeature.RESULTS_SHOW_DETAILS, item: {id: this.id}}) &&
      shouldDisablePrimaryLinkColumn() &&
      EntityStore.currentUser()
    );
  }

  hasTestOrders(): boolean {
    return BenchlingConfig.SAMPLE_TESTS_ENABLE;
  }
  hasRelevantItems(): boolean {
    return BenchlingConfig.INVENTORY_RELEVANT_ITEMS;
  }

  override initialize(...args: Array<unknown>): void {
    // @ts-expect-error
    super.initialize(...args);
    this.changeListener = this.changeListener.bind(this);
    this.registerListeners();

    const buffers = new navigation.BufferStates([new navigation.BufferState(), new navigation.BufferState()]);
    this.set({buffers});
    buffers
      .at(0)
      .tabs()
      .add(new ContainerTabState({containerId: this.id}));

    if (this.hasResults()) {
      buffers
        .at(1)
        .tabs()
        .add(new StorableResultsTabState({storableId: this.id}));
    }
    if (this.hasTestOrders()) {
      buffers
        .at(1)
        .tabs()
        .add(new ContainerTestOrdersTabState({modelId: this.id}));
    }
    if (this.hasRelevantItems()) {
      buffers
        .at(1)
        .tabs()
        .add(new ContainerRelevantItemsTabState({containerId: this.id}));
    }
    this.initIsWorkspaceSplitFromPreference();
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name', () => {
      this.syncTitleAndURL();
    });
  }

  changeListener(): void {
    const container = StorableStore.getContainer(this.id);
    if (container) {
      this.set({name: container.name, archiveRecord: container.archiveRecord});
    }
  }

  registerListeners(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    StorableStore.addChangeListener(this.changeListener);
  }

  cleanUp(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    StorableStore.removeChangeListener(this.changeListener);
  }
}

ContainerFileState.fileType = ModelTypeConstants.CONTAINER;

ContainerFileState.openFile = (fileId: string): JQueryPromise<ContainerFileState> =>
  asJQueryPromise(Promise.resolve(new ContainerFileState({id: fileId})));
